import type { ProductDetailItem, ProductListItem, ProductPriceInfo } from "~~/models/products";
import { PlanktonPredefinedRight } from "~~/server/plankton";

export function usePricing(
  products: Ref<Array<ProductListItem> | ProductDetailItem | null | undefined>,
  options: { loggedIn: boolean },
) {
  // keep state to prevent too many error toasts at once
  let previousState = "";

  // keep track of loading state to display in UI
  const isPricesLoading = ref(options.loggedIn);

  const { t } = useI18n();
  const { path } = useRoute();
  const { user } = useUserSession();

  const showPriceAvailable = hasAllRights(user.value, PlanktonPredefinedRight.ShowPriceNetAvailable, PlanktonPredefinedRight.ShowPriceGrossAvailable);

  // automatically create pricing request data from products, this will be watched by useAsyncData
  const requestData = computed(() => {
    const items = Array.isArray(products.value) ? products as Ref<Array<ProductListItem>> : ref([products.value]);

    if (!items.value || !options.loggedIn) {
      return [];
    }

    return items.value
      .filter(p => p !== null && p !== undefined)
      .flatMap((product) => {
        if (Array.isArray(product.overpacks) && product.overpacks.length > 0) {
          const requestList = product.overpacks.map(overpack => ({
            number: product.number,
            overpack: overpack.code,
          }));

          // if the product is variable weight, we need to add a request for the base product with "kg" overpack
          if (product.isVariableWeight) {
            requestList.push({ number: product.number, overpack: "kg" });
          }

          return requestList;
        }

        return [{ number: product.number, overpack: "" }];
      });
  });

  // unique key to prevent nuxt from sharing data
  const key = computed(() =>
    `api-ecom-products-prices-${path}-${import.meta.server}-${requestData.value.map(p => `${p.number}-${p.overpack}`).join(",")}`);

  const { data: prices, status }
    = useAsyncData<Array<ProductPriceInfo>>(
      // in nuxt 3.17, the key can be a computed
      key.value,
      () => {
        if (requestData.value.length === 0) {
          return Promise.resolve([] as Array<ProductPriceInfo>);
        }

        return $fetch("/api/ecom/products/prices", {
          method: "POST",
          body: { items: requestData.value },
        });
      },
      {
        server: false,
        lazy: true,
        immediate: options.loggedIn,
        watch: [requestData],
      },
    );

  watch(status, () => {
    // console.log("pricing status changed", status.value);
    switch (status.value) {
      case "idle":
        previousState = "";
        isPricesLoading.value = options.loggedIn && prices.value === null;
        break;

      case "pending":
        isPricesLoading.value = true;
        break;

      case "error":
        isPricesLoading.value = false;

        if (!showPriceAvailable) {
          return;
        }

        // TODO: we could add some handling to not try to get pricing for a while
        if (status.value !== previousState) {
          previousState = status.value;
          useFailToast(t("price-service.unavailable.title"), t("price-service.unavailable.text"));
        }

        break;

      default:
        // console.log(" product price status done?", prices?.value.length, "prices", key.value);
        isPricesLoading.value = false;
        break;
    }
  }, {
    immediate: options.loggedIn,
  });

  // experimental
  function extractProductPrices(
    input: Ref<Array<ProductPriceInfo>>,
    product: MaybeRef<ProductListItem>,
  ): ComputedRef<Array<ProductPriceInfo>> {
    const productRef = ref(product);

    return computed(() => {
      if (import.meta.server || !productRef.value) {
        return [];
      }

      return input.value?.filter((price: ProductPriceInfo) => price.productNumber === productRef.value?.number) ?? [];
    });
  };

  return {
    prices: prices,
    key: key,
    isPricesLoading: isPricesLoading,
    extractProductPrices: extractProductPrices,
  };
}
